import { useCallback } from 'react';

import { Icons } from 'app/constants/icons';
import { explorerUrl } from 'app/utils/cosmos.utils';

import { CosmosChainId } from 'chains/cosmos/cosmos.types';
import { EVMChainId } from 'chains/evm/evm.types';

import { NotificationItemProps } from '../components/core/Notifications/Notifications';
import { useNotifications } from './useNotifications';
import { ExternalLink } from 'app/shared/buttons/ExternalLink';

export const useTransactionNotifications = () => {
  const {notify} = useNotifications();
  const className =
    'relative flex light:border-2 border-0 border-white w-full !px-[17px] !py-6.5 !justify-start items-center gap-3.5 break-words rounded-xl light:bg-white bg-dark-gunmetal-500 text-lg leading-normal !font-bold shadow-[5px_5px_20px_0_rgba(0,0,0,0.10)]';

  const notifyTransactionSuccess = useCallback(
    (
      chainId: CosmosChainId | EVMChainId,
      txHash: string,
      notification?: NotificationItemProps,
    ) => {
      const href = explorerUrl(chainId, 'transaction', txHash);

      return notify({
        close: true,
        duration: 6000,
        className,
        content: (
          <>
            <span className="flex h-8 w-8 min-w-8 items-center justify-center rounded-full bg-success">
              <Icons.check size={18} strokeWidth={2.5} />
            </span>

            <div>
              <h6>Transaction submitted</h6>

              <ExternalLink href={href}>View transaction ID</ExternalLink>
            </div>
          </>
        ),
        ...notification,
      });
    },
    [notify],
  );

  const notifyTransactionError = useCallback(
    (error: string, notification?: NotificationItemProps) =>
      notify({
        close: true,
        duration: 11000,
        className,
        content: (
          <>
            <span className="flex h-8 w-8 min-w-8 items-center justify-center rounded-full bg-error">
              <Icons.x size={18} strokeWidth={2.5} />
            </span>

            <div>
              <h6 className="mb-[3px]">Transaction Failed</h6>

              <p className="break-words text-3xs font-normal leading-normal text-neutral-100 light:text-neutral-500">
                {error}
              </p>
            </div>
          </>
        ),
        ...notification,
      }),
    [notify],
  );

  const notifyTransactionLoading = useCallback(
    (notification?: NotificationItemProps) =>
      notify({
        close: true,
        duration: Infinity,
        className,
        content: (
          <>
            <div className="border-transparent h-8.75 w-8.75 animate-spin rounded-[50%] border-4 border-t-[#0360FF]" />

            <div>
              <h6 className="mb-[3px]">Transaction Broadcasting</h6>

              <p className="break-words text-3xs font-normal leading-normal text-neutral-100 light:text-neutral-500">
                Waiting for transaction to be signed and broadcasted
              </p>
            </div>
          </>
        ),
        ...notification,
      }),
    [notify],
  );

  return {
    notifyTransactionError,
    notifyTransactionSuccess,
    notifyTransactionLoading,
  };
};
